import { CREATE_NOTE, UPDATE_NOTE } from "@lib/graphql/mutations";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Eye,
  Lock,
  MessageSquare,
  Pencil,
  Plus,
  User,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useMutation } from "urql";
import { CopyButton } from "./CopyButton";

interface NoteData {
  id: string;
  content: string;
  noteType: string;
  visibility: string;
  author: { id: string; name: string };
  createdAt: string;
}

interface NotesPanelProps {
  notableType: "Campaign" | "Candidate" | "Client" | "CampaignCandidate";
  notableId: string;
  notes: NoteData[];
  onChanged: () => void;
  variant?: "expanded" | "list";
}

function notePreview(content: string): string {
  const stripped = content
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_`>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return stripped.length > 140 ? `${stripped.slice(0, 140)}…` : stripped;
}

function noteTitle(content: string): string {
  const firstLine = content.split("\n").find((line) => line.trim().length > 0) ?? "";
  const cleaned = firstLine
    .replace(/^#{1,6}\s+/, "")
    .replace(/[*_`]/g, "")
    .trim();
  return cleaned.length > 80 ? `${cleaned.slice(0, 80)}…` : cleaned || "Untitled note";
}

const NOTE_TYPE_COLORS: Record<string, string> = {
  general: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  call_summary: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  feedback: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  ai_generated: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};

const NOTE_TYPE_LABELS: Record<string, string> = {
  general: "General",
  call_summary: "Call Summary",
  feedback: "Feedback",
  ai_generated: "AI Generated",
};

const CREATABLE_TYPES = ["general", "call_summary", "feedback"];

const VISIBILITY_LABELS: Record<string, string> = {
  internal: "Internal",
  client_visible: "Client Visible",
  candidate_visible: "Candidate Visible",
};

const VISIBILITY_COLORS: Record<string, string> = {
  internal: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  client_visible: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  candidate_visible: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
};

const VISIBILITY_ICONS: Record<string, typeof Lock> = {
  internal: Lock,
  client_visible: Eye,
  candidate_visible: User,
};

export function NotesPanel({
  notableType,
  notableId,
  notes,
  onChanged,
  variant = "expanded",
}: NotesPanelProps) {
  const [showForm, setShowForm] = useState(false);
  const [content, setContent] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [visibility, setVisibility] = useState("internal");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedBodyRef = useRef<HTMLDivElement>(null);
  const [{ fetching: creating }, createNote] = useMutation(CREATE_NOTE);
  const [{ fetching: updating }, updateNote] = useMutation(UPDATE_NOTE);

  const selectedNote =
    variant === "list" && selectedId ? (notes.find((n) => n.id === selectedId) ?? null) : null;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    const result = await createNote({
      input: { notableType, notableId, content, noteType, visibility },
    });
    if (result.data?.createNote?.errors?.length === 0) {
      setContent("");
      setNoteType("general");
      setVisibility("internal");
      setShowForm(false);
      onChanged();
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editContent.trim()) return;
    const result = await updateNote({ input: { id, content: editContent } });
    if (result.data?.updateNote?.errors?.length === 0) {
      setEditingId(null);
      onChanged();
    }
  };

  const startEdit = (note: NoteData) => {
    setEditContent(note.content);
    setEditingId(note.id);
  };

  const renderBadges = (note: NoteData) => {
    const VisIcon = VISIBILITY_ICONS[note.visibility] ?? Lock;
    return (
      <>
        <span
          className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${NOTE_TYPE_COLORS[note.noteType] ?? NOTE_TYPE_COLORS.general}`}
        >
          {NOTE_TYPE_LABELS[note.noteType] ?? note.noteType}
        </span>
        <span
          className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${VISIBILITY_COLORS[note.visibility] ?? VISIBILITY_COLORS.internal}`}
          title={VISIBILITY_LABELS[note.visibility] ?? note.visibility}
        >
          <VisIcon size={8} />
          {VISIBILITY_LABELS[note.visibility] ?? note.visibility}
        </span>
      </>
    );
  };

  const renderNoteBody = (note: NoteData) =>
    editingId === note.id ? (
      <div className="space-y-2">
        <textarea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          rows={6}
          className="w-full rounded border border-border bg-surface px-3 py-2 text-xs"
        />
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => handleUpdate(note.id)}
            disabled={updating}
            className="rounded p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
          >
            <Check size={12} />
          </button>
          <button
            type="button"
            onClick={() => setEditingId(null)}
            className="rounded p-1 text-text-faint hover:bg-card-hover"
          >
            <X size={12} />
          </button>
        </div>
      </div>
    ) : (
      <div className="prose prose-xs prose-neutral dark:prose-invert max-w-none text-text-secondary notes-prose">
        <Markdown remarkPlugins={[remarkGfm]}>{note.content}</Markdown>
      </div>
    );

  const formSection = showForm && (
    <form onSubmit={handleCreate} className="mb-3 space-y-2 rounded border border-border p-3">
      <div className="flex gap-2">
        <select
          value={noteType}
          onChange={(e) => setNoteType(e.target.value)}
          className="rounded border border-border bg-surface px-2 py-1 text-xs"
        >
          {CREATABLE_TYPES.map((t) => (
            <option key={t} value={t}>
              {NOTE_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <select
          value={visibility}
          onChange={(e) => setVisibility(e.target.value)}
          className="rounded border border-border bg-surface px-2 py-1 text-xs"
        >
          {Object.entries(VISIBILITY_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Write a note..."
        rows={3}
        className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
        required
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={creating}
          className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90"
        >
          {creating ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          onClick={() => setShowForm(false)}
          className="rounded border border-border px-3 py-1.5 text-xs text-text-muted hover:bg-card-hover"
        >
          Cancel
        </button>
      </div>
    </form>
  );

  if (variant === "list" && selectedNote) {
    return (
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              setSelectedId(null);
              setEditingId(null);
            }}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-secondary hover:bg-card-hover"
          >
            <ArrowLeft size={12} /> Back to notes
          </button>
          <div className="flex items-center gap-1">
            {editingId !== selectedNote.id && (
              <CopyButton
                ariaLabel="Copy note"
                withLabel
                size={12}
                getContent={() => {
                  const el = selectedBodyRef.current;
                  if (!el) return { html: "", text: "" };
                  return {
                    html: el.innerHTML,
                    text: el.innerText || el.textContent || "",
                  };
                }}
              />
            )}
            {editingId !== selectedNote.id && (
              <button
                type="button"
                onClick={() => startEdit(selectedNote)}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
                title="Edit"
              >
                <Pencil size={12} /> Edit
              </button>
            )}
          </div>
        </div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {renderBadges(selectedNote)}
          <span className="text-[10px] text-text-faint">
            {selectedNote.author.name} &middot;{" "}
            {new Date(selectedNote.createdAt).toLocaleDateString()}
          </span>
        </div>
        <div ref={selectedBodyRef}>{renderNoteBody(selectedNote)}</div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare size={14} className="text-text-faint" />
          <h3 className="text-sm font-medium text-text-secondary">Notes</h3>
          <span className="text-xs text-text-faint">{notes.length}</span>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
          >
            <Plus size={12} /> Add Note
          </button>
        )}
      </div>

      {formSection}

      {notes.length === 0 && !showForm ? (
        <p className="py-4 text-center text-xs text-text-faint">No notes yet</p>
      ) : variant === "list" ? (
        <ul className="divide-y divide-border rounded border border-border">
          {notes.map((note) => (
            <li key={note.id}>
              <button
                type="button"
                onClick={() => setSelectedId(note.id)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-card-hover"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {renderBadges(note)}
                    <span className="text-[10px] text-text-faint">
                      {note.author.name} &middot; {new Date(note.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="truncate text-xs font-medium text-text-secondary">
                    {noteTitle(note.content)}
                  </div>
                  <div className="truncate text-[11px] text-text-muted">
                    {notePreview(note.content)}
                  </div>
                </div>
                <ChevronRight size={14} className="shrink-0 text-text-faint" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="space-y-2">
          {notes.map((note) => (
            <div key={note.id} className="rounded border border-border p-3">
              <div className="mb-1 flex items-center gap-2">
                {renderBadges(note)}
                <span className="text-[10px] text-text-faint">
                  {note.author.name} &middot; {new Date(note.createdAt).toLocaleDateString()}
                </span>
                {editingId !== note.id && (
                  <button
                    type="button"
                    onClick={() => startEdit(note)}
                    className="rounded p-0.5 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                    title="Edit"
                  >
                    <Pencil size={10} />
                  </button>
                )}
              </div>
              {renderNoteBody(note)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
